
import { useContext } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';

// redux
import { ComponentType } from 'redux/schemas/models/lecture-component';

import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { getAlphabetCharacter } from 'quizzes/components/utils';
import { gray5, success } from 'styles/global_defaults/colors';
import { largeSpacing } from 'styles/global_defaults/scaffolding';
import { ProgressiveQuizMode } from 'quizzes/components/mode';
import QuestionSetContext from 'lecture_pages/services/question-set-context';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';

type BigCheckboxProps = {
  index: number;
  color: string;
  checked: boolean;
  selected: boolean;
  mode: ProgressiveQuizMode;
  onChange: (newChecked) => void;
  className?: string;
  characterClassName?: string,
  optionId: number,
  circularBorder?: boolean,
};

const BigCheckbox = (props: BigCheckboxProps) => {
  const {
    mode,
    index,
    color,
    selected,
    onChange,
    className,
    checked = false,
    characterClassName,
    optionId,
    circularBorder = false,
  } = props;

  const { lectureComponent } = useContext(QuestionSetContext);
  const {
    isCurrentActivityType,
    dataQaQuestionSetAttributes,
  } = useQuestionSetHelpers();

  const editNotFocusedBorderColor = checked ? success : gray5;

  const styles = css`
    border-radius: ${circularBorder ? '50%' : '4px'};
    width: ${largeSpacing}px;
    height: ${largeSpacing}px;
    border: 1px solid ${gray5};

    .icon {
      color: ${success};
    }

    ${mode === ProgressiveQuizMode.EDIT ? css`
      border: 1px solid ${editNotFocusedBorderColor};
    ` : css`
      ${selected && css`
        border: 1px solid ${color};
        .text-large-body {
          color: ${color};
          font-weight: bold;
        }
      `};
    `};
  `;

  return (
    <NvTooltip
      text={t.QUIZZES.MAKE_CORRECT_ANSWER()}
      enabled={mode === ProgressiveQuizMode.EDIT && !checked && !isCurrentActivityType(ComponentType.SURVEY)}
    >
      <ClickableContainer
        css={styles}
        onClick={() => onChange(!checked)}
        layoutOnly={mode !== ProgressiveQuizMode.EDIT || isCurrentActivityType(ComponentType.SURVEY)}
        className={`align-items-center justify-content-center ${className}`}
        data-qa={dataQaQuestionSetAttributes.questionModal.optionCorrectCheckbox}
        data-qa-id={`${dataQaQuestionSetAttributes.questionModal.optionCorrectCheckbox}_${optionId}`}
      >
        {mode === ProgressiveQuizMode.EDIT ? (checked && (
          <NvIcon icon='check' size='smallest' className='d-flex' />
        )) : (
          <div className={`text-large-body gray-1 ${characterClassName}`}>
            {getAlphabetCharacter(index)}
          </div>
        )}
      </ClickableContainer>
    </NvTooltip>
  );
};

export default BigCheckbox;
